/**
 * Role-based authorization utilities
 */

import { UserRole, UserOut } from '../types/api';

/**
 * Check if user is a super admin
 */
export function isSuperAdmin(user: UserOut | null): boolean {
  return user?.role === 'super_admin';
}

/**
 * Check if user is an organizer
 */
export function isOrganizer(user: UserOut | null): boolean {
  return user?.role === 'organizer';
}

/**
 * Check if user can access all events (super admin or organizer)
 */
export function canAccessAllEvents(user: UserOut | null): boolean {
  if (!user) return false;
  return isSuperAdmin(user) || user.role === 'organizer';
}

/**
 * Check if user can manage global settings (super admin only)
 */
export function canManageGlobalSettings(user: UserOut | null): boolean {
  return isSuperAdmin(user);
}

/**
 * Check if user can manage country tax rates (super admin only)
 */
export function canManageCountryTaxRates(user: UserOut | null): boolean {
  return isSuperAdmin(user);
}

/**
 * Check if user can see all users (super admin only)
 */
export function canSeeAllUsers(user: UserOut | null): boolean {
  return isSuperAdmin(user);
}

/**
 * Check if user is warehouse/logistics staff
 */
export function isWarehouseStaff(user: UserOut | null): boolean {
  return user?.role === 'warehouse';
}

/**
 * Check if user can manage warehouse stock (warehouse staff, organizer, or super admin)
 */
export function canManageWarehouse(user: UserOut | null): boolean {
  if (!user) return false;
  return isWarehouseStaff(user) || isOrganizer(user) || isSuperAdmin(user);
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role: UserRole): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin';
    case 'organizer':
      return 'Organizer';
    case 'barman':
      return 'Barman';
    case 'customer':
      return 'Customer';
    case 'warehouse':
      return 'Warehouse Logistics';
    default:
      return role;
  }
}

/**
 * Get role badge color
 */
export function getRoleColor(role: UserRole): string {
  switch (role) {
    case 'super_admin':
      return 'var(--error-color)';
    case 'organizer':
      return 'var(--primary-color)';
    case 'barman':
      return 'var(--success-color)';
    case 'customer':
      return 'var(--info-color)';
    case 'warehouse':
      return 'var(--warning-color)';
    default:
      return 'var(--text-secondary)';
  }
}

